import { Grid, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import NumberFormat from 'react-number-format';
import { CerebroHeading, CerebroLabel, CerebroText } from '../../shared/components';
import {
	Deal,
	DealAnalysisMetricType,
	DealFinancialMetric,
	DealFinancialMetricType,
	DealFinancialMetricTypeMap,
} from '../../shared/models';
import { NumberUtils } from '../../shared/utils';
import { getYearLabel, MetricYears } from './IncomeStatementHeadings';

interface IncomeStatementRowProps {
	isLoading: boolean;
	deal: Deal | null | undefined;
	financialMetrics: DealFinancialMetric[];
	metricType: DealFinancialMetricType | undefined;
	analysisType: DealAnalysisMetricType | undefined;
	columns: FinancialCoulmnsProps;
}

export const IncomeStatementRow: React.FC<IncomeStatementRowProps> = ({
	isLoading,
	deal,
	metricType,
	analysisType,
	financialMetrics,
	columns,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	if (metricType === undefined) return null;

	const dealCreatedOn = deal?.financialStartDate;

	const isAdjustedEbitda = metricType === 'AdjustedEbitda';
	const isMrr = metricType === 'Mrr';
	const blankColumnWidth = getBlankColumnWidth(columns);
	const showBlankColumn = !isMobile && blankColumnWidth > 0;

	return (
		<>
			<Grid item xs={12} md={2} sx={{ display: 'flex' }} alignItems="center">
				<CerebroHeading size="h3" gutterBottom sx={{ marginTop: { xs: 1, md: 0 } }}>
					{DealFinancialMetricTypeMap[metricType]}
				</CerebroHeading>
			</Grid>

			{!isMrr && columns.twoYearsAnyData && (
				<Grid item xs={12} md={2}>
					{isMobile && (
						<CerebroLabel gutterBottom>
							{isLoading && <Skeleton height={50} />}
							{!isLoading && getYearLabel('twoYearsAgo', dealCreatedOn)}
						</CerebroLabel>
					)}

					{isLoading && <Skeleton height={75} />}

					{isAdjustedEbitda && !isLoading && (
						<CerebroText align="right" weight="medium">
							{NumberUtils.FormatNumber(
								adjustEbitda(financialMetrics, 'twoYearsAgo', analysisType),
								'currency',
								NumberUtils.NoDecimalsFormatOptions
							)}
						</CerebroText>
					)}

					{!isAdjustedEbitda && !isLoading && (
						<CerebroText align="right" weight="medium">
							<NumberFormat
								value={getFinancialValue(financialMetrics, 'twoYearsAgo', analysisType, metricType)}
								displayType="text"
								thousandSeparator={true}
								prefix="$"
							/>
						</CerebroText>
					)}
				</Grid>
			)}

			{!isMrr && columns.previousYearAnyData && (
				<Grid item xs={12} md={2}>
					{isMobile && (
						<CerebroLabel gutterBottom>
							{isLoading && <Skeleton height={50} />}
							{!isLoading && getYearLabel('previousFullYear', dealCreatedOn)}
						</CerebroLabel>
					)}

					{isLoading && <Skeleton height={75} />}

					{isAdjustedEbitda && !isLoading && (
						<CerebroText align="right" weight="medium">
							{NumberUtils.FormatNumber(
								adjustEbitda(financialMetrics, 'previousFullYear', analysisType),
								'currency',
								NumberUtils.NoDecimalsFormatOptions
							)}
						</CerebroText>
					)}

					{!isAdjustedEbitda && !isLoading && (
						<CerebroText align="right" weight="medium">
							<NumberFormat
								value={getFinancialValue(
									financialMetrics,
									'previousFullYear',
									analysisType,
									metricType
								)}
								displayType="text"
								thousandSeparator={true}
								prefix="$"
							/>
						</CerebroText>
					)}
				</Grid>
			)}

			<Grid item xs={12} md={2}>
				{isMobile && (
					<CerebroLabel gutterBottom>
						{isLoading && <Skeleton height={50} />}
						{!isLoading && getYearLabel('trailingTwelveMonths', dealCreatedOn)}
					</CerebroLabel>
				)}

				{isLoading && <Skeleton height={75} />}

				{isAdjustedEbitda && !isLoading && (
					<CerebroText align="right" weight="medium">
						{NumberUtils.FormatNumber(
							adjustEbitda(financialMetrics, 'trailingTwelveMonths', analysisType),
							'currency',
							NumberUtils.NoDecimalsFormatOptions
						)}
					</CerebroText>
				)}
				{!isAdjustedEbitda && !isLoading && (
					<CerebroText align="right" weight="medium">
						<NumberFormat
							value={getFinancialValue(
								financialMetrics,
								'trailingTwelveMonths',
								analysisType,
								metricType
							)}
							displayType="text"
							thousandSeparator={true}
							prefix="$"
						/>
					</CerebroText>
				)}
			</Grid>

			{!isMrr && columns.projectedFullYearAnyData && (
				<Grid item xs={12} md={2}>
					{isMobile && (
						<CerebroLabel gutterBottom>
							{isLoading && <Skeleton height={50} />}
							{!isLoading && getYearLabel('projectedFullYear', dealCreatedOn)}
						</CerebroLabel>
					)}

					{isLoading && <Skeleton height={75} />}

					{isAdjustedEbitda && !isLoading && (
						<CerebroText align="right" weight="medium">
							{NumberUtils.FormatNumber(
								adjustEbitda(financialMetrics, 'projectedFullYear', analysisType),
								'currency',
								NumberUtils.NoDecimalsFormatOptions
							)}
						</CerebroText>
					)}

					{!isAdjustedEbitda && !isLoading && (
						<CerebroText align="right" weight="medium">
							<NumberFormat
								value={getFinancialValue(
									financialMetrics,
									'projectedFullYear',
									analysisType,
									metricType
								)}
								displayType="text"
								thousandSeparator={true}
								prefix="$"
							/>
						</CerebroText>
					)}
				</Grid>
			)}

			{!isMrr && columns.futureYearAnyData && (
				<Grid item xs={12} md={2}>
					{isMobile && (
						<CerebroLabel gutterBottom>
							{isLoading && <Skeleton height={50} />}
							{!isLoading && getYearLabel('futureYear', dealCreatedOn)}
						</CerebroLabel>
					)}

					{isLoading && <Skeleton height={75} />}

					{isAdjustedEbitda && !isLoading && (
						<CerebroText align="right" weight="medium">
							{NumberUtils.FormatNumber(
								adjustEbitda(financialMetrics, 'futureYear', analysisType),
								'currency',
								NumberUtils.NoDecimalsFormatOptions
							)}
						</CerebroText>
					)}

					{!isAdjustedEbitda && !isLoading && (
						<CerebroText align="right" weight="medium">
							<NumberFormat
								value={getFinancialValue(financialMetrics, 'futureYear', analysisType, metricType)}
								displayType="text"
								thousandSeparator={true}
								prefix="$"
							/>
						</CerebroText>
					)}
				</Grid>
			)}

			{showBlankColumn && (
				<Grid item md={blankColumnWidth}>
					&nbsp;
				</Grid>
			)}
		</>
	);
};

export const adjustEbitda = (
	metrics: DealFinancialMetric[],
	year: MetricYears,
	analysisType: DealAnalysisMetricType | undefined
) => {
	return (
		getFinancialValue(metrics, year, analysisType, 'Ebitda') +
		getFinancialValue(metrics, year, analysisType, 'Adjustments')
	);
};

export const getFinancialValue = (
	metrics: DealFinancialMetric[],
	year: MetricYears,
	analysisMetricType?: DealAnalysisMetricType,
	financialMetricType?: DealFinancialMetricType
): number => {
	const filtered = metrics.filter(
		w => w.analysisMetricType == analysisMetricType && w.financialMetricType == financialMetricType
	);
	if (filtered.length > 0) {
		const metric = filtered[0];

		switch (year) {
			case 'futureYear':
				return metric.futureYear ?? 0;
			case 'previousFullYear':
				return metric.previousFullYear ?? 0;
			case 'trailingTwelveMonths':
				return metric.trailingTwelveMonths ?? 0;
			case 'projectedFullYear':
				return metric.projectedFullYear ?? 0;
			case 'twoYearsAgo':
				return metric.twoYearsAgo ?? 0;
		}
	}

	return 0;
};

export interface FinancialCoulmnsProps {
	twoYearsAnyData: boolean;
	previousYearAnyData: boolean;
	projectedFullYearAnyData: boolean;
	futureYearAnyData: boolean;
}

export function getBlankColumnWidth(columns: FinancialCoulmnsProps): number {
	let count = 0;
	if (!columns.twoYearsAnyData) count++;
	if (!columns.previousYearAnyData) count++;
	if (!columns.projectedFullYearAnyData) count++;
	if (!columns.futureYearAnyData) count++;

	count = count * 2;

	return count;
}
